import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, ArrowLeft, Eye, EyeOff, CheckCircle, XCircle, KeyRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_eeeed7c0-b14c-41e9-a1b8-b122278f0c69/artifacts/auk1nki7_LOGO-EECASA.png";

export const ResetPasswordPage = () => {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Password strength checks
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password),
  };

  const allChecksPassed = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = password === confirmPassword && password.length > 0;

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenValid(false);
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`);
        setTokenValid(response.data.valid);
      } catch (err) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allChecksPassed) {
      setError(t('resetPassword.errors.requirementsNotMet'));
      return;
    }

    if (!passwordsMatch) {
      setError(t('resetPassword.errors.passwordsDontMatch'));
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('resetPassword.errors.resetError'));
    } finally {
      setLoading(false);
    }
  };

  const PasswordCheck = ({ passed, label }) => (
    <div className={`flex items-center gap-2 text-sm ${passed ? 'text-green-600' : 'text-gray-400'}`}>
      {passed ? (
        <CheckCircle className="w-4 h-4" />
      ) : (
        <XCircle className="w-4 h-4" />
      )}
      <span>{label}</span>
    </div>
  );

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-[#F8F9FA] dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#E63946] mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t('resetPassword.verifying')}</p>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-[#F8F9FA] dark:bg-gray-900 font-['IBM_Plex_Sans',sans-serif]">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-md border border-gray-100 dark:border-gray-700">
              <img 
                src={LOGO_URL} 
                alt="Especialistas En Casa" 
                className="h-16 object-contain"
              />
            </div>
          </div>

          <Card className="border-0 shadow-lg dark:bg-gray-800">
            <CardHeader className="space-y-1 text-center">
              <div className="flex justify-center mb-2">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
              </div>
              <CardTitle className="text-2xl font-semibold font-['Work_Sans',sans-serif] text-gray-900 dark:text-gray-100">
                {t('resetPassword.invalidLink.title')}
              </CardTitle>
              <CardDescription className="text-gray-500 dark:text-gray-400">
                {t('resetPassword.invalidLink.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                {t('resetPassword.invalidLink.expiryNote')}
              </p>
              
              <Link to="/forgot-password" className="block">
                <Button className="w-full bg-[#E63946] hover:bg-[#C92A36]">
                  {t('resetPassword.invalidLink.requestNew')}
                </Button>
              </Link>
              
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full text-gray-600 dark:text-gray-400">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('resetPassword.backToLogin')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#F8F9FA] dark:bg-gray-900 font-['IBM_Plex_Sans',sans-serif]">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-md border border-gray-100 dark:border-gray-700">
            <img 
              src={LOGO_URL} 
              alt="Especialistas En Casa" 
              className="h-16 object-contain"
            />
          </div>
        </div>

        <Card className="border-0 shadow-lg dark:bg-gray-800">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl font-semibold font-['Work_Sans',sans-serif] text-gray-900 dark:text-gray-100">
              {success ? t('resetPassword.newPassword.titleSuccess') : t('resetPassword.newPassword.title')}
            </CardTitle>
            <CardDescription className="text-gray-500 dark:text-gray-400">
              {success 
                ? t('resetPassword.newPassword.descriptionSuccess')
                : t('resetPassword.newPassword.description')
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                </div>
                
                <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                  {t('resetPassword.success.message')}
                </p>

                <Button
                  className="w-full bg-[#E63946] hover:bg-[#C92A36]"
                  onClick={() => navigate('/login')}
                  data-testid="go-to-login"
                >
                  <KeyRound className="mr-2 h-4 w-4" />
                  {t('resetPassword.success.signIn')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800">
                    <AlertDescription className="text-red-700 dark:text-red-300">
                      {error}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-700 dark:text-gray-300">
                    {t('resetPassword.form.newPassword')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11 pr-10 border-gray-300 dark:border-gray-600 focus:border-[#E63946] focus:ring-[#E63946]"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Password strength indicator */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('resetPassword.form.requirements')}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <PasswordCheck passed={passwordChecks.length} label={t('resetPassword.form.minLength')} />
                    <PasswordCheck passed={passwordChecks.uppercase} label={t('resetPassword.form.uppercase')} />
                    <PasswordCheck passed={passwordChecks.lowercase} label={t('resetPassword.form.lowercase')} />
                    <PasswordCheck passed={passwordChecks.number} label={t('resetPassword.form.number')} />
                    <PasswordCheck passed={passwordChecks.special} label={t('resetPassword.form.specialChar')} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-gray-700 dark:text-gray-300">
                    {t('resetPassword.form.confirmPassword')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className={`h-11 pr-10 border-gray-300 dark:border-gray-600 focus:border-[#E63946] focus:ring-[#E63946] ${
                        confirmPassword && !passwordsMatch ? 'border-red-300' : ''
                      }`}
                      data-testid="reset-password-confirm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {confirmPassword && !passwordsMatch && (
                    <p className="text-sm text-red-500">{t('resetPassword.form.passwordsDontMatch')}</p>
                  )}
                  {passwordsMatch && (
                    <p className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      {t('resetPassword.form.passwordsMatch')}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading || !allChecksPassed || !passwordsMatch}
                  className="w-full h-11 bg-[#E63946] hover:bg-[#C92A36] text-white font-medium transition-colors disabled:opacity-50"
                  data-testid="reset-password-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('resetPassword.form.submitting')}
                    </>
                  ) : (
                    t('resetPassword.form.submit')
                  )}
                </Button>

                <Link to="/login" className="block">
                  <Button variant="ghost" className="w-full text-gray-600 dark:text-gray-400">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t('resetPassword.backToLogin')}
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          © 2024 Especialistas En Casa. Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
